import { FlatOrganisationsQuery } from 'src/graphql/_generated';

/**
 * Base interface for data that can be converted to a tree structure.
 * Any data type used with tree utilities must have these fields.
 */
export interface TreeCompatibleData {
  id: string;
  parentId?: string | null;
  isArchived: boolean;
}

/**
 * Tree node with children. Extends the base data type with a children array.
 */
export type TreeNode<T extends TreeCompatibleData> = T & {
  children: TreeNode<T>[];
};

/**
 * PrimeReact TreeTable node structure.
 * Used by TreeTable component to render hierarchical data.
 */
export interface TreeTableNode<T extends TreeCompatibleData> {
  key: string;
  data: TreeNode<T>;
  children: TreeTableNode<T>[];
  level: number;
}

/**
 * Flat organisation item from the GraphQL query.
 * Used by OrgTable and AccessTable components.
 */
export type FlatOrgData = FlatOrganisationsQuery['flatOrganisations'][number];

/**
 * PrimeReact TreeTable node structure for flat organisation data.
 */
export type FlatOrgTreeTableNode = TreeTableNode<FlatOrgData>;

/**
 * Builds a tree structure from a flat array of items.
 * Items with parentId are nested under their parent nodes.
 *
 * @param items - Flat array of items with id and parentId
 * @param from - Optional ID to build tree from a specific node
 * @param showArchived - Whether to include archived items (default: false)
 * @returns Tree structure with hierarchical parent-child relationships
 */
export const buildTreeFromFlat = <T extends TreeCompatibleData>(
  items: T[],
  from?: string,
  showArchived = false,
): TreeNode<T>[] => {
  const filteredItems = showArchived ? items : items.filter((item) => !item.isArchived);

  const nodeMap = new Map<string, TreeNode<T>>();
  const tree: TreeNode<T>[] = [];

  filteredItems.forEach((item) => {
    nodeMap.set(item.id, { ...item, children: [] } as TreeNode<T>);
  });

  filteredItems.forEach((item) => {
    const node = nodeMap.get(item.id)!;

    if (item.parentId) {
      const parent = nodeMap.get(item.parentId);
      if (parent) {
        parent.children.push(node);
      } else {
        tree.push(node);
      }
    } else {
      tree.push(node);
    }
  });

  if (from) {
    const startNode = nodeMap.get(from);
    return startNode ? [startNode] : [];
  }

  return tree;
};

/**
 * Converts TreeNode structure to TreeTableNode format required by PrimeReact TreeTable.
 *
 * @param nodes - Tree structure of nodes
 * @param level - Current nesting level (default: 1 for root)
 * @returns Array of TreeTableNode objects with key, data, children, and level properties
 */
export const convertToTreeTableFormat = <T extends TreeCompatibleData>(
  nodes: TreeNode<T>[],
  level = 1,
): TreeTableNode<T>[] => {
  return nodes.map((node) => ({
    key: node.id,
    data: node,
    children: node.children ? convertToTreeTableFormat(node.children, level + 1) : [],
    level,
  }));
};
